import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import ShareButtons from "@/components/ShareButtons";
import MapPicker from "@/components/MapPicker";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Heart, MagnifyingGlass, MapPin, ShieldCheck } from "@phosphor-icons/react";

export default function AlertePublique() {
  const { doudou_id } = useParams();
  const [doudou, setDoudou] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/public/doudou/${doudou_id}`)
      .then(({ data }) => { if (!cancelled) setDoudou(data); })
      .catch(() => { if (!cancelled) setError("Cette alerte n'existe pas ou a été retirée."); });
    return () => { cancelled = true; };
  }, [doudou_id]);

  if (error) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="alerte-not-found">
          <Heart size={56} weight="duotone" className="text-rose-300 mx-auto" />
          <h1 className="font-display text-3xl font-black text-stone-800 mt-4">Alerte introuvable</h1>
          <p className="text-stone-600 mt-2">{error}</p>
          <Link to="/" className="inline-block mt-6">
            <Button className="rounded-full bg-orange-500 hover:bg-orange-600 px-6 py-6 font-bold">Retour à l'accueil</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  if (!doudou) {
    return <Layout><div className="max-w-2xl mx-auto px-4 py-20 text-stone-500" data-testid="alerte-loading">Chargement…</div></Layout>;
  }

  const isLost = doudou.status === "lost";
  const shareUrl = `${window.location.origin}/alerte/${doudou.doudou_id}`;
  const shareTitle = isLost
    ? `🧸 ${doudou.name} a été perdu ${doudou.department ? `dans le ${doudou.department}` : ""}. Aidez ${doudou.owner_first_name || "la famille"} à le retrouver !`
    : `🧸 ${doudou.name} est rentré à la maison grâce à Doudou Alert.`;

  return (
    <Layout>
      <SEO
        title={`Alerte ${doudou.name}`}
        description={`Doudou perdu : ${doudou.name}. ${doudou.description?.slice(0, 140) || "Aidez-nous à le retrouver."}`}
      />
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="rounded-3xl bg-white/80 backdrop-blur shadow-xl border border-rose-100 overflow-hidden" data-testid="alerte-card">
          {/* Header banner */}
          <div className={`px-6 py-5 ${isLost ? "bg-gradient-to-r from-rose-500 to-orange-500" : "bg-gradient-to-r from-emerald-500 to-emerald-600"} text-white`}>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider opacity-90">
              {isLost ? <><MagnifyingGlass size={16} weight="bold" /> Alerte doudou perdu</> : <><Heart size={16} weight="fill" /> Retrouvé !</>}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-black mt-1" data-testid="alerte-name">{doudou.name}</h1>
            <p className="text-white/90 mt-1 text-sm">
              {isLost ? `${doudou.owner_first_name || "Une famille"} cherche son doudou` : "Cette histoire s'est bien terminée"}
            </p>
          </div>

          {/* Body */}
          <div className="p-6 grid sm:grid-cols-[220px_1fr] gap-6">
            <div className="aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-rose-100 to-orange-100 grid place-items-center" data-testid="alerte-photo">
              {doudou.photo ? <img src={doudou.photo} alt={doudou.name} className="w-full h-full object-contain" /> : <Heart size={80} weight="duotone" className="text-rose-300" />}
            </div>
            <div>
              <p className="text-stone-700 leading-relaxed" data-testid="alerte-description">
                {doudou.description || "Aucune description fournie."}
              </p>
              {doudou.department && (
                <p className="text-stone-500 text-sm mt-3 flex items-center gap-1.5">
                  <MapPin size={16} weight="duotone" className="text-orange-500" />
                  Département : <strong className="text-stone-700">{doudou.department}</strong>
                </p>
              )}
              {isLost && (
                <Link to={`/trouve?doudou=${doudou.doudou_id}`} className="inline-block mt-5" data-testid="alerte-found-cta">
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-6 py-6 font-bold shadow-lg shadow-orange-500/30">
                    <MagnifyingGlass size={18} weight="bold" className="mr-2" /> J'ai vu ce doudou !
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Lost map */}
          {isLost && doudou.lost_location && (
            <div className="px-6 pb-2">
              <h2 className="font-display text-lg font-bold text-stone-800 flex items-center gap-2 mb-3">
                <MapPin size={18} weight="duotone" className="text-rose-500" /> Dernière position connue
              </h2>
              <MapPicker value={{ lat: doudou.lost_location.lat, lng: doudou.lost_location.lng }} readOnly height={260} />
            </div>
          )}

          {/* Share */}
          <div className="p-6 border-t border-rose-100 bg-rose-50/40">
            <h2 className="font-display text-lg font-bold text-stone-800 mb-3">Partagez cette alerte</h2>
            <p className="text-sm text-stone-600 mb-4">Plus l'alerte est partagée, plus la chance de retrouver {doudou.name} grandit 🌟</p>
            <ShareButtons url={shareUrl} title={shareTitle} />
          </div>

          {/* Trust banner */}
          <div className="px-6 py-4 text-xs text-stone-500 flex items-center gap-2 border-t border-rose-100">
            <ShieldCheck size={16} weight="duotone" className="text-emerald-500" />
            Plateforme française gratuite · Aucune coordonnée n'est rendue publique
          </div>
        </div>
      </div>
    </Layout>
  );
}
